import React, {useEffect, useMemo, useState} from 'react'
import {
  Box,
  Grid,
  Stack,
  Typography,
  TextField,
  Autocomplete,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import RefreshIcon from '@mui/icons-material/Refresh'

import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined'

import {
  FormCard,
  FormHeader,
  FormHeaderIconBox,
  FormHeaderTextGroup,
  FormSectionTitle,
  FormSectionSubtitle,
  FormBody,
} from './styled'

import {
  useBulkServiceability,
  SHIPMENT_TYPE_B2B,
  pincodeTypesFor,
} from './useBulkServiceability'
import {StatusDrawer, HistoryDrawer} from './Drawers'
import {isAdmin} from '../../helpers/ReduxHelpers'

// shipmentType: 'B2B' = PTL couriers (pickup + drop counts, no COD / whitelist / L1); left out = all columns.
export default function AccountCodesTable({parentCps = [], shipmentType}) {
  const {
    fetchAccountCodes,
    fetchAccountCodeStatus,
    deactivateAccountCode,
    downloadAccountCodeCsv,
  } = useBulkServiceability({shipmentType})
  const isB2b = shipmentType === SHIPMENT_TYPE_B2B
  const {types: pincodeTypes, labels: pincodeTypeLabels} = pincodeTypesFor(shipmentType)
  const columns = isB2b
    ? ['Account Code', 'Type', 'Pickup', 'Drop', 'Last Updated', 'Actions']
    : ['Account Code', 'Type', 'Pickup', 'Drop Prepaid', 'Drop COD', 'Whitelist', 'L1', 'Last Updated', 'Actions']

  const [parentCp, setParentCp] = useState('')
  const [accountCodes, setAccountCodes] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  // Frontend-only enrichment: the list API doesn't include merchant-scope
  // fields, so we fan out per-row /status calls after the list loads and merge
  // the scope data into a {accountCode: {l1MerchantScope, l1EnabledMerchantIds}} map.
  // The map is what the Whitelist column reads. We keep it separate from the
  // row data so a refresh of the table doesn't clobber an in-flight enrichment.
  const [scopeMap, setScopeMap] = useState({})

  // drawer state
  const [statusFor, setStatusFor] = useState(null)
  const [historyFor, setHistoryFor] = useState(null)

  // delete dialog
  const [deactivateTarget, setDeactivateTarget] = useState(null)
  const [isDeactivating, setIsDeactivating] = useState(false)

  // download menu
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null)
  const [downloadMenuFor, setDownloadMenuFor] = useState(null)

  const userIsSuperAdmin = useMemo(() => {
    try {
      return isAdmin()
    } catch {
      return false
    }
  }, [])

  useEffect(() => {
    if (!parentCp) return
    let cancelled = false
    setIsLoading(true)
    // Wipe stale scope data when the parent CP changes — otherwise the column
    // shows the previous courier's whitelist counts briefly.
    setScopeMap({})
    fetchAccountCodes(parentCp).then((data) => {
      if (cancelled) return
      setAccountCodes(data?.accountCodes || [])
      setIsLoading(false)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentCp, refreshKey])

  // After the account-codes list lands, fan out /status calls (in parallel)
  // for every row to pull the merchant-scope fields the list response doesn't
  // include. The Whitelist cell then renders from `scopeMap` keyed on
  // accountCode. Concurrency is capped so a parent with 50+ codes doesn't
  // hammer the backend in a single burst.
  useEffect(() => {
    // B2B lists have no merchant scope (B2B allocation does not read it): no Whitelist column to fill.
    if (isB2b || !accountCodes || accountCodes.length === 0) return
    let cancelled = false
    const codes = accountCodes
      .map((c) => c.accountCode)
      .filter((c) => c && scopeMap[c] === undefined) // skip ones already loaded
    if (codes.length === 0) return

    const CONCURRENCY = 6
    const queue = [...codes]
    const next = async () => {
      while (!cancelled) {
        const code = queue.shift()
        if (!code) return
        const data = await fetchAccountCodeStatus(code)
        if (cancelled) return
        setScopeMap((prev) => ({
          ...prev,
          [code]: {
            l1MerchantScope: data?.l1MerchantScope || null,
            l1EnabledMerchantIds: data?.l1EnabledMerchantIds || [],
          },
        }))
      }
    }
    Promise.all(Array.from({length: Math.min(CONCURRENCY, queue.length)}, next))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountCodes])

  const refresh = () => {
    // Drop scope data on refresh so it re-pulls fresh status — otherwise
    // toggling a scope via the drawer wouldn't be reflected in the column.
    setScopeMap({})
    setRefreshKey((k) => k + 1)
  }

  const handleDownload = async (code, type) => {
    setDownloadMenuAnchor(null)
    await downloadAccountCodeCsv(code, type)
  }

  const handleDeactivate = async () => {
    if (!deactivateTarget) return
    setIsDeactivating(true)
    const r = await deactivateAccountCode(deactivateTarget.accountCode)
    setIsDeactivating(false)
    setDeactivateTarget(null)
    if (r) refresh()
  }

  return (
    <>
      <FormCard>
        <FormHeader>
          <FormHeaderIconBox>
            <VisibilityOutlinedIcon fontSize='small' />
          </FormHeaderIconBox>
          <FormHeaderTextGroup>
            <FormSectionTitle>Account Codes</FormSectionTitle>
            <FormSectionSubtitle>
              {isB2b
                ? 'Browse PTL account codes by parent courier. View pickup and drop pincode counts, view history, or download the saved pincode CSV.'
                : 'Browse account codes by parent courier. View L1 status and merchant whitelist, view history, or download the saved pincode CSV.'}
            </FormSectionSubtitle>
          </FormHeaderTextGroup>
        </FormHeader>
        <FormBody>
          <Grid container spacing={2} alignItems='center' sx={{mb: 2}}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                size='small'
                options={parentCps}
                value={parentCp || null}
                onChange={(_, v) => setParentCp(v || '')}
                renderInput={(params) => (
                  <TextField {...params} label='Parent Courier' placeholder='Pick a courier' />
                )}
              />
            </Grid>
            {parentCp && (
              <Grid item xs={12} md={8}>
                <Stack direction='row' spacing={1} justifyContent='flex-end' alignItems='center'>
                  <Typography sx={{fontSize: '0.72rem', color: 'grey.600'}}>
                    {accountCodes.length} account code{accountCodes.length === 1 ? '' : 's'}
                  </Typography>
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={refresh}
                    disabled={isLoading}
                    startIcon={<RefreshIcon fontSize='small' />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      borderColor: 'grey.300',
                      color: 'grey.800',
                      '&:hover': {borderColor: 'grey.400', backgroundColor: '#fafafa'},
                    }}
                  >
                    Refresh
                  </Button>
                </Stack>
              </Grid>
            )}
          </Grid>

          {!parentCp ? (
            <EmptyState
              icon={<RouteOutlinedIcon sx={{fontSize: 28, color: 'grey.500'}} />}
              title='Pick a parent courier to begin'
              subtitle={isB2b
                ? 'Select a courier above to load its PTL account codes and pincode counts.'
                : 'Select a courier above to load its account codes, pincode counts, and L1 status.'}
            />
          ) : isLoading ? (
            <Stack alignItems='center' sx={{py: 6}}>
              <CircularProgress size={24} />
            </Stack>
          ) : accountCodes.length === 0 ? (
            <EmptyState
              icon={<InboxOutlinedIcon sx={{fontSize: 28, color: 'grey.500'}} />}
              title={`No account codes for ${parentCp}`}
              subtitle='No child account codes are configured for this parent courier yet.'
            />
          ) : (
            <TableContainer
              sx={{
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 1.5,
                maxHeight: '60vh',
              }}
            >
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map((h) => (
                      <TableCell
                        key={h}
                        align={['Pickup', 'Drop', 'Drop Prepaid', 'Drop COD', 'Whitelist'].includes(h) ? 'right' : 'left'}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          color: 'grey.700',
                          backgroundColor: '#fafafa',
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accountCodes.map((c) => (
                    <TableRow key={c.accountCode} hover>
                      <TableCell>
                        <Stack direction='row' spacing={0.75} alignItems='center'>
                          <Typography
                            sx={{
                              fontSize: '0.82rem',
                              fontWeight: 600,
                              color: 'grey.800',
                              lineHeight: 1.3,
                            }}
                          >
                            {c.accountCode}
                          </Typography>
                          {!c.serviceabilityLoaded && (
                            <Tooltip title='No pincode data uploaded yet'>
                              <Box
                                component='span'
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  height: 18,
                                  px: 0.75,
                                  borderRadius: 0.75,
                                  fontSize: '0.6rem',
                                  fontWeight: 700,
                                  letterSpacing: '0.04em',
                                  textTransform: 'uppercase',
                                  color: 'grey.700',
                                  backgroundColor: '#f5f5f5',
                                  border: '1px solid #e0e0e0',
                                }}
                              >
                                Empty
                              </Box>
                            </Tooltip>
                          )}
                          {Array.isArray(c.pdServiceTypes) &&
                            c.pdServiceTypes.map((st) => (
                              <Tooltip key={st} title={`PD (SDD/NDD) data loaded: ${st}`}>
                                <Box
                                  component='span'
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    height: 18,
                                    px: 0.75,
                                    borderRadius: 0.75,
                                    fontSize: '0.6rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                    color: '#6a1b9a',
                                    backgroundColor: '#f3e5f5',
                                    border: '1px solid #e1bee7',
                                  }}
                                >
                                  {st}
                                </Box>
                              </Tooltip>
                            ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Box
                          component='span'
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            height: 20,
                            px: 1,
                            borderRadius: 0.75,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            backgroundColor:
                              c.courierType === 'FORWARD' ? '#e3f2fd' : '#f3e5f5',
                            color: c.courierType === 'FORWARD' ? '#1565c0' : '#6a1b9a',
                          }}
                        >
                          {c.courierType}
                        </Box>
                      </TableCell>
                      <TableCell align='right'>
                        <CountChip value={c.pickupPincodeCount} accent='#1565c0' />
                      </TableCell>
                      <TableCell align='right'>
                        <CountChip value={c.dropPrepaidPincodeCount} accent='#2e7d32' />
                      </TableCell>
                      {!isB2b && (
                        <>
                          <TableCell align='right'>
                            <CountChip value={c.dropCodPincodeCount} accent='#e65100' />
                          </TableCell>
                          <TableCell align='right'>
                            <WhitelistCell scope={scopeMap[c.accountCode]} />
                          </TableCell>
                          <TableCell>
                            <Box
                              component='span'
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                height: 20,
                                px: 1,
                                borderRadius: 0.75,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                color: c.l1Enabled ? '#2e7d32' : 'grey.600',
                                backgroundColor: c.l1Enabled ? '#e8f5e9' : '#f5f5f5',
                                border: c.l1Enabled ? '1px solid #c8e6c9' : '1px solid #e0e0e0',
                              }}
                            >
                              {c.l1Enabled ? 'Enabled' : 'Disabled'}
                            </Box>
                          </TableCell>
                        </>
                      )}
                      <TableCell sx={{fontSize: '0.72rem', color: 'grey.600'}}>
                        {c.lastUpdated ? new Date(c.lastUpdated).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell>
                        <Stack direction='row' spacing={0.5}>
                          <ActionIconButton
                            tooltip='View Status'
                            color='#1565c0'
                            bg='#e3f2fd'
                            onClick={() => setStatusFor(c.accountCode)}
                          >
                            <VisibilityOutlinedIcon sx={{fontSize: 16}} />
                          </ActionIconButton>
                          <ActionIconButton
                            tooltip='Upload History'
                            color='#6a1b9a'
                            bg='#f3e5f5'
                            onClick={() => setHistoryFor(c.accountCode)}
                          >
                            <HistoryOutlinedIcon sx={{fontSize: 16}} />
                          </ActionIconButton>
                          <ActionIconButton
                            tooltip='Download CSV'
                            color='#2e7d32'
                            bg='#e8f5e9'
                            onClick={(e) => {
                              setDownloadMenuFor(c)
                              setDownloadMenuAnchor(e.currentTarget)
                            }}
                          >
                            <DownloadIcon sx={{fontSize: 16}} />
                          </ActionIconButton>
                          {userIsSuperAdmin && (
                            <ActionIconButton
                              tooltip='Deactivate'
                              color='#c62828'
                              bg='#ffebee'
                              onClick={() => setDeactivateTarget(c)}
                            >
                              <DeleteOutlineIcon sx={{fontSize: 16}} />
                            </ActionIconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </FormBody>
      </FormCard>

      {/* Download menu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={!!downloadMenuAnchor}
        onClose={() => setDownloadMenuAnchor(null)}
      >
        {pincodeTypes.map((t) => {
          const row = downloadMenuFor
          const count =
            t === 'PICKUP_PREPAID'
              ? row?.pickupPincodeCount
              : t === 'DROP_PREPAID'
              ? row?.dropPrepaidPincodeCount
              : row?.dropCodPincodeCount
          return (
            <MenuItem
              key={t}
              onClick={() => downloadMenuFor && handleDownload(downloadMenuFor.accountCode, t)}
              disabled={!count}
            >
              <Stack direction='row' justifyContent='space-between' sx={{width: '100%'}} spacing={2}>
                <Typography sx={{fontSize: '0.8rem'}}>{pincodeTypeLabels[t]}</Typography>
                <Typography sx={{fontSize: '0.7rem', color: 'grey.600'}}>
                  {(count || 0).toLocaleString()}
                </Typography>
              </Stack>
            </MenuItem>
          )
        })}
      </Menu>

      {/* Drawers */}
      <StatusDrawer
        open={!!statusFor}
        accountCode={statusFor}
        shipmentType={shipmentType}
        onClose={() => setStatusFor(null)}
      />
      <HistoryDrawer
        open={!!historyFor}
        accountCode={historyFor}
        shipmentType={shipmentType}
        onClose={() => setHistoryFor(null)}
      />
      {/* Helpers below */}
      {/* Deactivate confirmation */}
      <Dialog open={!!deactivateTarget} onClose={() => !isDeactivating && setDeactivateTarget(null)}>
        <DialogTitle>Deactivate account code?</DialogTitle>
        <DialogContent>
          <Typography sx={{fontSize: '0.85rem', mb: 1}}>
            Account code: <code>{deactivateTarget?.accountCode}</code>
          </Typography>
          <Typography sx={{fontSize: '0.78rem', color: 'grey.700'}}>
            {isB2b ? (
              <>
                This sets <code>isActive = false</code>. B2B allocation stops reading these lists, so the courier
                is allowed on every lane again (as with no list). Pincode data is <strong>not deleted</strong> — a new
                upload makes it active again.
              </>
            ) : (
              <>
                This sets <code>isActive = false</code>. The account code becomes invisible to all L1 checks.
                Pincode data is <strong>not deleted</strong> — you can reactivate later.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)} disabled={isDeactivating}>
            Cancel
          </Button>
          <Button
            color='error'
            variant='contained'
            onClick={handleDeactivate}
            disabled={isDeactivating}
            startIcon={isDeactivating ? <CircularProgress size={14} /> : null}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

/* ─── Helper components ─────────────────────────────────────────────────── */

function EmptyState({icon, title, subtitle}) {
  return (
    <Stack alignItems='center' sx={{py: 6, px: 3, textAlign: 'center'}} spacing={1}>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography sx={{fontSize: '0.92rem', fontWeight: 700, color: 'grey.800'}}>{title}</Typography>
      {subtitle && (
        <Typography sx={{fontSize: '0.78rem', color: 'grey.600', maxWidth: 360, lineHeight: 1.5}}>
          {subtitle}
        </Typography>
      )}
    </Stack>
  )
}

function ActionIconButton({tooltip, color, bg, onClick, children}) {
  return (
    <Tooltip title={tooltip}>
      <IconButton
        size='small'
        onClick={onClick}
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1,
          color: `${color} !important`,
          backgroundColor: `${bg}80`, // 50% alpha so the row keeps breathing
          transition: 'background-color 0.12s ease, transform 0.12s ease',
          '&:hover': {
            backgroundColor: bg,
            transform: 'translateY(-1px)',
          },
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  )
}

function CountChip({value, accent}) {
  const v = value || 0
  const muted = v === 0
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 52,
        px: 1,
        py: 0.25,
        borderRadius: 1.5,
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 700,
        fontSize: '0.8rem',
        color: muted ? 'grey.400' : accent,
        backgroundColor: muted ? 'transparent' : `${accent}14`,
      }}
    >
      {muted ? '—' : v.toLocaleString()}
    </Box>
  )
}

// Shows the merchant-scope status for a row. "All" badge when the account code
// serves every merchant; numeric count when it's a whitelist; "Disabled" when
// the whitelist is empty (effectively off).
//
// The scope data is fetched per-row via a background fan-out of /status calls
// from the parent component (frontend-only — list API doesn't carry these
// fields). While that fetch is in flight we show a faint spinner; once loaded
// the cell renders the badge.
function WhitelistCell({scope}) {
  // scope === undefined → still loading
  // scope === null      → /status returned but no data (account code may have no BulkServiceability doc yet)
  if (scope === undefined) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 52,
        }}
      >
        <CircularProgress size={12} thickness={5} sx={{color: 'grey.400'}} />
      </Box>
    )
  }

  const scopeVal = scope?.l1MerchantScope
  const ids = scope?.l1EnabledMerchantIds || []
  const count = ids.length

  if (!scopeVal) {
    return (
      <Tooltip title='No merchant scope set'>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 52,
            fontSize: '0.78rem',
            color: 'grey.400',
          }}
        >
          —
        </Box>
      </Tooltip>
    )
  }

  if (scopeVal === 'ALL') {
    return (
      <Tooltip title='Serves all merchants'>
        <Box
          component='span'
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 52,
            px: 1,
            py: 0.25,
            borderRadius: 1.5,
            fontWeight: 700,
            fontSize: '0.7rem',
            color: '#1565c0',
            backgroundColor: '#e3f2fd',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          All
        </Box>
      </Tooltip>
    )
  }

  // WHITELIST scope
  const muted = count === 0
  return (
    <Tooltip
      title={
        muted
          ? 'Empty whitelist — L1 is disabled for this account code'
          : `${count} whitelisted merchant${count === 1 ? '' : 's'}`
      }
    >
      <Box
        component='span'
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 52,
          px: 1,
          py: 0.25,
          borderRadius: 1.5,
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 700,
          fontSize: '0.8rem',
          color: muted ? '#c62828' : '#e65100',
          backgroundColor: muted ? '#ffebee' : '#fff3e0',
        }}
      >
        {muted ? '0' : count.toLocaleString()}
      </Box>
    </Tooltip>
  )
}
